<template>
  <div>
    <b-card
      v-for="ipEntry in ipEntries"
      :key="ipEntry.ip"
      class="mb-3"
      no-body
    >
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Related evidence for {{ ipEntry.ip }}</h5>
      </b-card-header>

      <b-card-body>
        <!-- Stix AttackPatterns -->
        <b-card v-if="hasIndicators(ipEntry)" no-body class="mb-3">
          <b-card-header class="py-2">
            <b-row class="mx-0" align-h="between" align-v="center">
              <b-col class="px-0" cols="12">
                <h6 class="mb-0">Attack Patterns</h6>
              </b-col>
            </b-row>
          </b-card-header>

          <b-card-body class="py-2">
            <b-table
              id="related-indicators"
              responsive
              small
              :items="indicatorRows(ipEntry)"
              :fields="indicatorFields"
              :per-page="perPage"
              :current-page="currentPageIndicators"
              :sort-by.sync="sortByIndicators"
              :sort-desc.sync="sortDescIndicators"
            >
              <template #cell(_details)="row">
                <b-button size="sm" variant="link" class="p-0" @click="row.toggleDetails">
                  {{ row.detailsShowing ? 'Hide' : 'View' }}
                </b-button>
              </template>

              <template #row-details="{ item }">
                <b-card class="mb-0" body-class="py-2">
                  <h6 class="mb-2">Attack Patterns</h6>

                  <b-table
                    small
                    responsive
                    fixed
                    :items="item.attackPatterns || []"
                    :fields="attackPatternFields"
                  >
                    <template #table-colgroup="{ fields }">
                      <col
                        v-for="f in fields"
                        :key="f.key"
                        :style="attackPatternsColStyle(f.key)"
                      />
                    </template>

                    <template #cell(description)="{ value }">
                      <div :style="wrapStyle">{{ value || '-' }}</div>
                    </template>

                    <template #head(stix_ids)>
                      <span v-b-tooltip.hover="stixTooltip">
                        STIX CTI
                      </span>
                    </template>

                    <template #cell(stix_ids)="{ value, item: apItem, index }">
                      <div v-if="Array.isArray(value) && value.length">
                        <template v-if="value.length <= 5">
                          <div v-for="id in value" :key="id">{{ id }}</div>
                        </template>

                        <template v-else>
                          <div
                            v-for="id in visibleStixIds(value, apItem, index)"
                            :key="id"
                          >
                            {{ id }}
                          </div>

                          <b-button
                            size="sm"
                            variant="link"
                            class="p-0"
                            @click="toggleStixIds(apItem, index)"
                          >
                            <template v-if="isStixExpanded(apItem, index)">
                              Show less
                            </template>
                            <template v-else>
                              … ({{ value.length - 5 }} more)
                            </template>
                          </b-button>
                        </template>
                      </div>

                      <span v-else>-</span>
                    </template>
                  </b-table>
                </b-card>
              </template>
            </b-table>

            <b-pagination
              v-if="indicatorRows(ipEntry).length > perPage"
              v-model="currentPageIndicators"
              :total-rows="indicatorRows(ipEntry).length"
              :per-page="perPage"
              first-number
              last-number
              align="right"
              class="mb-0 mt-1"
            />
          </b-card-body>
        </b-card>

        <!-- Stix Malware -->
        <b-card v-if="hasAnyMalware(ipEntry)" no-body class="mb-3">
          <b-card-header class="py-2">
            <b-row class="mx-0" align-h="between" align-v="center">
              <b-col class="px-0" cols="12">
                <h6 class="mb-0">Malware</h6>
              </b-col>
            </b-row>
          </b-card-header>

          <b-card-body class="py-2">
            <b-table
              id="related-malware"
              responsive
              small
              :items="malwareIndicatorRows(ipEntry)"
              :fields="malwareIndicatorFields"
              :per-page="perPage"
              :current-page="currentPageMalware"
              :sort-by.sync="sortByMalware"
              :sort-desc.sync="sortDescMalware"
            >
              <template #cell(_details)="row">
                <b-button size="sm" variant="link" class="p-0" @click="row.toggleDetails">
                  {{ row.detailsShowing ? 'Hide' : 'View' }}
                </b-button>
              </template>

              <template #row-details="{ item }">
                <b-card class="mb-0" body-class="py-2">
                  <h6 class="mb-2">Malware</h6>

                  <b-table
                    small
                    responsive
                    :items="item.malware || []"
                    :fields="malwareFields"
                  >

                    <template #head(stix_ids)>
                      <span v-b-tooltip.hover="stixTooltip">
                        STIX CTI
                      </span>
                    </template>

                    <template #cell(stix_ids)="{ value, item: mwItem, index }">
                      <div v-if="Array.isArray(value) && value.length">
                        <template v-if="value.length <= 5">
                          <div v-for="id in value" :key="id">{{ id }}</div>
                        </template>

                        <template v-else>
                          <div
                            v-for="id in visibleStixIds(value, mwItem, index)"
                            :key="id"
                          >
                            {{ id }}
                          </div>

                          <b-button
                            size="sm"
                            variant="link"
                            class="p-0"
                            @click="toggleStixIds(mwItem, index)"
                          >
                            <template v-if="isStixExpanded(mwItem, index)">
                              Show less
                            </template>
                            <template v-else>
                              … ({{ value.length - 5 }} more)
                            </template>
                          </b-button>
                        </template>
                      </div>

                      <span v-else>-</span>
                    </template>
                  </b-table>
                </b-card>
              </template>
            </b-table>

            <b-pagination
              v-if="malwareIndicatorRows(ipEntry).length > perPage"
              v-model="currentPageMalware"
              :total-rows="malwareIndicatorRows(ipEntry).length"
              :per-page="perPage"
              first-number
              last-number
              align="right"
              class="mb-0 mt-1"
            />
          </b-card-body>
        </b-card>

        <!-- Behaviors -->
        <b-card v-if="hasBehaviors(ipEntry)" no-body class="mb-3">
          <b-card-header class="py-2">
            <b-row class="mx-0" align-h="between" align-v="center">
              <b-col class="px-0" cols="12">
                <h6 class="mb-0">Behaviors</h6>
              </b-col>
            </b-row>
          </b-card-header>

          <b-card-body class="py-2">
            <b-table
              id="related-behaviors"
              responsive
              small
              :items="behaviorRows(ipEntry.behaviors)"
              :fields="behaviorFields"
              :per-page="perPage"
              :current-page="currentPageBehaviors"
            />
          </b-card-body>
        </b-card>

        <!-- Hosts -->
        <b-card v-if="hasHosts(ipEntry)" no-body class="mb-3">
          <b-card-header class="py-2">
            <b-row class="mx-0" align-h="between" align-v="center">
              <b-col class="px-0" cols="12">
                <h6 class="mb-0">Hosts</h6>
              </b-col>
            </b-row>
          </b-card-header>

          <b-card-body class="py-2">
            <b-table
              id="related-hosts"
              responsive
              small
              :items="hostRows(ipEntry.hosts)"
              :fields="hostFields"
              :per-page="perPage"
              :current-page="currentPageHosts"
            />
          </b-card-body>
        </b-card>

        <!-- Package / Vuln Pairs -->
        <b-card v-if="hasPackageVulnPairs(ipEntry)" no-body class="mb-3">
          <b-card-header class="py-2">
            <b-row class="mx-0" align-h="between" align-v="center">
              <b-col class="px-0" cols="12">
                <h6 class="mb-0">Package / Vuln Pairs</h6>
              </b-col>
            </b-row>
          </b-card-header>

          <b-card-body class="py-2">
            <b-table
              id="related-package-vuln"
              responsive
              small
              :items="packageVulnRows(ipEntry.packageVulnPairs)"
              :fields="packageVulnFields"
              :per-page="perPage"
              :current-page="currentPagePkgVuln"
            >
              <template #cell(_details)="row">
                <b-button size="sm" variant="link" class="p-0" @click="row.toggleDetails">
                  {{ row.detailsShowing ? 'Hide' : 'View' }}
                </b-button>
              </template>

              <template #row-details="{ item }">
                <b-card class="mb-0" body-class="py-2">
                  <h6 class="mb-2">Vulnerabilities</h6>

                  <b-table
                    small
                    responsive
                    :items="item.vulnerabilities || []"
                    :fields="vulnerabilityFields"
                  >
                    <template #cell(description)="{ value }">
                      <div :style="wrapStyle">{{ value || '-' }}</div>
                    </template>
                  </b-table>
                </b-card>
              </template>
            </b-table>
          </b-card-body>
        </b-card>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
export default {
  name: 'ThreatRelatedEvidenceTable',
  props: {
    enrichments: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      perPage: 10,

      currentPageIndicators: 1,
      sortByIndicators: null,
      sortDescIndicators: false,

      currentPageMalware: 1,
      sortByMalware: null,
      sortDescMalware: false,

      currentPageHosts: 1,
      currentPagePkgVuln: 1,
      currentPageBehaviors: 1,

      stixExpanded: {},

      stixTooltip:
        'Cyber Threat Intelligence (CTI) in Structured Threat Information Expression (STIX) format',
    };
  },

  computed: {
    wrapStyle() {
      return {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
      };
    },

    ipEntries() {
      const rows = (this.enrichments || []).reduce((acc, wrapper) => {
        const map = wrapper?.ipThreatEnrichments ? wrapper.ipThreatEnrichments : {};
        const entries = Object.entries(map).map(([ip, obj]) => ({
          ip,
          ...(obj || {}),
        }));
        return acc.concat(entries);
      }, []);
      return rows.filter((entry) => this.hasAnyEvidence(entry));
    },

    indicatorFields() {
      return [
        { key: 'scope', label: 'Scope', sortable: true },
        { key: 'entity', label: 'Entity', sortable: true },
        { key: 'indicatorName', label: 'Indicator', sortable: true },
        { key: 'indicatorPattern', label: 'Pattern' },
        { key: 'attackPatternsCount', label: 'Attack Patterns', sortable: true },
        { key: '_details', label: '' },
      ];
    },

    malwareIndicatorFields() {
      return [
        { key: 'scope', label: 'Scope', sortable: true },
        { key: 'entity', label: 'Entity', sortable: true },
        { key: 'indicatorName', label: 'Indicator', sortable: true },
        { key: 'indicatorPattern', label: 'Pattern' },
        { key: 'malwareCount', label: 'Malware', sortable: true },
        { key: '_details', label: '' },
      ];
    },

    attackPatternFields() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'mitre_id', label: 'MITRE ID' },
        { key: 'description', label: 'Description' },
        { key: 'stix_ids', label: 'STIX CTI' },
      ];
    },

    malwareFields() {
      return [
        { key: 'name', label: 'Name' },
        { key: 'stix_ids', label: 'STIX CTI' },
      ];
    },

    behaviorFields() {
      return [{ key: 'name', label: 'Name' }];
    },

    hostFields() {
      return [
        { key: 'hostName', label: 'Host' },
        { key: 'osType', label: 'OS' },
      ];
    },

    packageVulnFields() {
      return [
        { key: 'packageName', label: 'Package' },
        { key: 'version', label: 'Version' },
        { key: 'publisher', label: 'Publisher' },
        { key: 'vulnCount', label: 'Vulnerabilities' },
        { key: '_details', label: '' },
      ];
    },

    vulnerabilityFields() {
      return [
        { key: 'id', label: 'CVE' },
        { key: 'description', label: 'Description' },
      ];
    },
  },

  methods: {
    attackPatternsColStyle(key) {
      switch (key) {
        case 'description':
          return { width: '60%' };
        case 'stix_ids':
          return { width: '20%' };
        case 'name':
          return { width: '10%' };
        case 'mitre_id':
          return { width: '10%' };
        default:
          return {};
      }
    },

    hasAnyEvidence(ipEntry) {
      if (!ipEntry) return false;

      return (
        this.indicatorRows(ipEntry).length > 0
        || this.malwareIndicatorRows(ipEntry).length > 0
        || this.hostRows(ipEntry.hosts).length > 0
        || this.packageVulnRows(ipEntry.packageVulnPairs).length > 0
        || this.behaviorRows(ipEntry.behaviors).length > 0
      );
    },

    hasIndicators(ipEntry) {
      return this.indicatorRows(ipEntry).length > 0;
    },

    hasAnyMalware(ipEntry) {
      return this.malwareIndicatorRows(ipEntry).length > 0;
    },

    hasHosts(ipEntry) {
      return this.hostRows(ipEntry?.hosts).length > 0;
    },

    hasBehaviors(ipEntry) {
      return this.behaviorRows(ipEntry?.behaviors).length > 0;
    },

    hasPackageVulnPairs(ipEntry) {
      return this.packageVulnRows(ipEntry?.packageVulnPairs).length > 0;
    },

    indicatorRows(ipEntry) {
      if (!ipEntry) return [];

      const rows = [];

      const ipArr = Array.isArray(ipEntry.ipAttackPatterns) ? ipEntry.ipAttackPatterns : [];
      ipArr.forEach((x, idx) => {
        const indicator = x?.indicator || {};
        const attackPatterns = Array.isArray(x?.attackPatterns) ? x.attackPatterns : [];
        rows.push({
          id: `ip-ap-${idx}`,
          scope: 'IP',
          entity: ipEntry.ip || '-',
          indicatorName: indicator.name || '-',
          indicatorPattern: indicator.pattern || '-',
          attackPatternsCount: attackPatterns.length,
          attackPatterns,
        });
      });

      const domainPairs = Array.isArray(ipEntry.domainAttackPatterns) ? ipEntry.domainAttackPatterns : [];
      domainPairs.forEach((pair, pairIdx) => {
        const domain = (pair?.domain?.domain || '').trim() || '-';
        const indicators = Array.isArray(pair?.indicators) ? pair.indicators : [];
        indicators.forEach((entry, indIdx) => {
          const indicator = entry?.indicator || {};
          const attackPatterns = Array.isArray(entry?.attackPatterns) ? entry.attackPatterns : [];
          rows.push({
            id: `dom-ap-${pairIdx}-${indIdx}`,
            scope: 'Domain',
            entity: domain,
            indicatorName: indicator.name || '-',
            indicatorPattern: indicator.pattern || '-',
            attackPatternsCount: attackPatterns.length,
            attackPatterns,
          });
        });
      });

      return rows.filter((r) => {
        const hasIndicator = (r.indicatorName && r.indicatorName !== '-')
          || (r.indicatorPattern && r.indicatorPattern !== '-');
        return hasIndicator || r.attackPatternsCount > 0;
      });
    },

    malwareIndicatorRows(ipEntry) {
      if (!ipEntry) return [];

      const rows = [];

      const ipArr = Array.isArray(ipEntry.ipMalwares) ? ipEntry.ipMalwares : [];
      ipArr.forEach((x, idx) => {
        const indicator = x?.indicator || {};
        const malware = Array.isArray(x?.malware) ? x.malware : [];
        rows.push({
          id: `ip-mw-${idx}`,
          scope: 'IP',
          entity: ipEntry.ip || '-',
          indicatorName: indicator.name || '-',
          indicatorPattern: indicator.pattern || '-',
          malwareCount: malware.length,
          malware,
        });
      });

      const domainPairs = Array.isArray(ipEntry.domainMalwares) ? ipEntry.domainMalwares : [];
      domainPairs.forEach((pair, pairIdx) => {
        const domain = (pair?.domain?.domain || '').trim() || '-';
        const indicators = Array.isArray(pair?.indicators) ? pair.indicators : [];
        indicators.forEach((entry, indIdx) => {
          const indicator = entry?.indicator || {};
          const malware = Array.isArray(entry?.malware) ? entry.malware : [];
          rows.push({
            id: `dom-mw-${pairIdx}-${indIdx}`,
            scope: 'Domain',
            entity: domain,
            indicatorName: indicator.name || '-',
            indicatorPattern: indicator.pattern || '-',
            malwareCount: malware.length,
            malware,
          });
        });
      });

      return rows.filter((r) => {
        const hasIndicator = (r.indicatorName && r.indicatorName !== '-')
          || (r.indicatorPattern && r.indicatorPattern !== '-');
        return hasIndicator || r.malwareCount > 0;
      });
    },

    behaviorRows(items) {
      const arr = Array.isArray(items) ? items : [];
      return arr
        .map((x, idx) => ({
          id: idx,
          name: x?.name || '-',
        }))
        .filter((x) => x.name !== '-');
    },

    hostRows(items) {
      const arr = Array.isArray(items) ? items : [];
      return arr
        .map((x, idx) => ({
          id: idx,
          hostName: x?.hostName || '-',
          osType: x?.osType || '-',
        }))
        .filter((x) => x.hostName !== '-' || x.osType !== '-');
    },

    packageVulnRows(items) {
      const arr = Array.isArray(items) ? items : [];

      const rows = arr.map((x, idx) => {
        const pkg = x?.package || {};
        const vulns = Array.isArray(x?.vulnerabilities) ? x.vulnerabilities : [];
        return {
          id: idx,
          packageName: pkg.name || '-',
          version: pkg.version || '-',
          publisher: pkg.publisher || '-',
          vulnCount: vulns.length,
          vulnerabilities: vulns,
        };
      });

      return rows.filter((r) => r.packageName !== '-' || r.vulnCount > 0);
    },

    stixKey(item, index) {
      const stable = item && (item.mitre_id || item.name) ? `${item.mitre_id || item.name}` : '';
      return stable ? `stix:${stable}:${index}` : `stix:${index}`;
    },

    isStixExpanded(item, index) {
      const key = this.stixKey(item, index);
      return !!this.stixExpanded[key];
    },

    toggleStixIds(item, index) {
      const key = this.stixKey(item, index);
      this.$set(this.stixExpanded, key, !this.stixExpanded[key]);
    },

    visibleStixIds(value, item, index) {
      if (!Array.isArray(value)) return [];
      return this.isStixExpanded(item, index) ? value : value.slice(0, 5);
    },
  },
};
</script>
